import { useMemo } from "react";
import type { z } from "zod";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import { createInitialValues, tk, workflows } from "@thalamos/vision-common";

import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type PreEndIncidentSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["preEndIncident"]["formDefinition"]["sections"][0]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function PreEndIncidentFormSkeleton() {
  return (
    <MultiPageFormSkeleton headerText={tk("preEndIncident.endFormTitle")} />
  );
}

///

export type PreEndIncidentFormProps<T extends WorkflowVersion> = {
  onSubmit: (value: PreEndIncidentSchema<T>) => Promise<void>;
  onCancel: () => void;
  workflowVersion: T;
  initialValues?: PreEndIncidentSchema<T>;
  incidentWorkItem?: IncidentWorkItemWithContext;
};

///

export function PreEndIncidentForm<T extends WorkflowVersion>(
  props: PreEndIncidentFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];

  const formSpecification =
    workflow?.preEndIncident.formDefinition.sections[0]?.buildFormSpecification(
      { incidentWorkItem: props.incidentWorkItem },
    );

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(
    () =>
      formSpecification === undefined
        ? {}
        : createInitialValues(
            formSpecification.strictFieldSchemas,
            props.initialValues,
          ),
    [props.initialValues, formSpecification],
  );

  if (formSpecification === undefined) {
    return <Error />;
  }

  const formSchema = formSpecification.formSchema;
  const strictFieldSchemas = formSpecification.strictFieldSchemas;

  return (
    <MultiPageForm<typeof formSchema>
      headerText={tk("preEndIncident.endFormTitle")}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
    />
  );
}
